/** @jsx jsx */
/**
 * Source file for generating embedded cart script using something resembling our regular React syntax.
 *
 * Special rules apply in this file due to limitations of the build/pack process that bundles a single script for usage outside of this site:
 * - `next/*` packages like `/image` and `/router` are not usable and will result in errors loading the script on the external site.
 * - bouweb Redux state will not be accessible in this component. Pass necessary configuration via props and return selections via query string on the checkout link.
 *
 * Notes:
 * - inline `css` props work because of the jsx pragma above, @see https://emotion.sh/docs/css-prop#jsx-pragma
 *
 */
import React from 'react';
import { jsx } from '@emotion/react';
import paymentOptions from '../../public/payment-options.png';
import { PRODUCT_CODES } from '../../app/cart/constants.js';
import InlineList from './elements/InlineList.js';
import LoadingWrapper from './elements/Loading.js';
import useDeferredInert from './hooks/useDeferredInert.js';
import { Checkmark, Close } from './theme/icons.js';
import OrderOnAmazonLink from './AmazonLinkEmbedded.js';
import useCartDisplayItems from './hooks/useCartDisplayItems.js';
import { CLOSE_TRANSITION_MS, Banner, CartWrapper, CheckoutButton, CloseButton, Footer, Header, HideArrow, PaymentOptionsImage, Title } from './cartPanelStyles.js';
import Summary from './CartSummary.js';
import Items from './CartItems.js';

// Referenced by the panel's `aria-labelledby`. A module constant rather than a
// generated id because exactly one cart panel exists per page.
const TITLE_ID = 'bec-cart-title';

const CartPanel = ({
    isCartPanelOpen,
    isLoading,
    onQuantityChange,
    onRemoveProduct,
    onClose,
    catalog,
    lineItems,
    config,
    pricing,
    totalQuantity,
    onCheckoutClicked,
}) => {
    const panelRef = useDeferredInert(isCartPanelOpen, CLOSE_TRANSITION_MS);

    const {
        displayLineItems,
        hasValidPricing,
        summary,
    } = useCartDisplayItems({ catalog, pricing, lineItems });

    return <CartWrapper
        ref={panelRef}
        isOpen={isCartPanelOpen}
        /**
         * `role="dialog"` without `aria-modal`: this is deliberately a *non-modal*
         * dialog. A modal (`<dialog>.showModal()` or similar) would make the rest
         * of the host page inert, lock its scrolling, and promote the panel to the
         * top layer — aggressive behavior for a widget on a page it doesn't own.
         * The trade-off is that focus is not trapped, so `useDeferredInert` above
         * and the Escape handling in Cart.js do that job by hand.
         *
         * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/dialog
         *
         * TODO: investigate Popover API: https://developer.mozilla.org/en-US/docs/Web/API/Popover_API
         */
        role="dialog"
        alt="Cart"
        aria-labelledby={TITLE_ID}
        // Tells assistive tech the contents are mid-update while pricing loads,
        // which the visual blur in `LoadingWrapper` conveys only to sighted users.
        aria-busy={isLoading}
    >
        <Header>
            <Title id={TITLE_ID}>Your bag ({totalQuantity})</Title>
            <CloseButton className='trigger-gtm-close-cart' onClick={onClose} title="Hide">
                <Close />
            </CloseButton>
        </Header>
        <Banner>
            <Checkmark /> Free UPS shipping
        </Banner>
        <LoadingWrapper isLoading={isLoading} /* TODO: figure out where loading seam is exactly */ >
            {pricing !== null &&
                <>
                    <Items
                        isLoading={isLoading}
                        onQuantityChange={onQuantityChange}
                        onRemoveProduct={onRemoveProduct}

                        displayLineItems={displayLineItems}
                        hasValidPricing={hasValidPricing}
                    />

                    <Summary
                        hasValidPricing={hasValidPricing}
                        summary={summary}
                    />
                </>
            }
            {pricing === null && <p>Loading pricing details...</p>} {/* TODO: loading animation? Does this only show on initial load? Should maybe be a skeleton instead */}
        </LoadingWrapper>
        <Footer>
            <CheckoutButton
                className='trigger-gtm-cart-checkout'
                disabled={isLoading || totalQuantity < 1}
                onClick={onCheckoutClicked}
            >
                Checkout
            </CheckoutButton>

            {/* TODO: remove when confirmed
            <PaymentOptionsImage src={paymentOptions} alt="payment options" />
            <OrderOnAmazonLink config={config} pricing={pricing} /> */}
            <InlineList items={['No contracts', 'Free shipping', '45-day guarantee']} />
        </Footer>
    </CartWrapper>;
};

export default CartPanel;
